import { useState } from 'react';
import toast from 'react-hot-toast';
import { useApi } from '../../utils/useApi';

/* The gym's view of online payments: which account collects, how money
   reaches it, and — when Gympe allows it — the choice between collecting via
   Gympe and the gym's own account. Credentials are managed by Gympe and never
   shown. Only rendered for gyms with online payments enabled. */

export interface PaymentSettings {
  enabled: boolean;
  active: { accountId: string; provider: string; mode: 'direct' | 'collect'; environment: 'test' | 'live' } | null;
  problem: string | null;
  canChoose: boolean;
  currentChoiceAccountId: string | null;
  options: Array<{ accountId: string; label: string; detail: string; mode: 'direct' | 'collect'; environment: string }>;
}

const PROBLEMS: Record<string, string> = {
  no_account: 'No payment account is assigned yet.',
  account_disabled: 'The payment account is disabled.',
  provider_unavailable: 'The payment gateway is unavailable.',
  account_not_owned: 'The assigned account belongs to another gym.',
  linked_account_missing: 'Your payout account is not verified yet (KYC pending).',
};

export default function PaymentSettingsSection({ gymId, settings, onChange }: {
  gymId: string;
  settings: PaymentSettings;
  onChange: () => void;
}) {
  const { fetchWithAuth } = useApi();
  const [saving, setSaving] = useState<string | null>(null);

  const choose = async (accountId: string) => {
    setSaving(accountId);
    try {
      await fetchWithAuth('/api/commerce/payment-settings', { method: 'PUT', body: JSON.stringify({ gymId, accountId }) });
      toast.success('Payment account updated. New payment links will use it.');
      onChange();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(null);
    }
  };

  const a = settings.active;
  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Payments</h2>
        <p className="text-sm text-gray-500 mt-1">How members pay you online through payment links. Set up and supported by Gympe.</p>
      </div>

      <div className="rounded-xl border border-gray-200 p-5">
        {a ? (
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-900">
              Collecting through {a.provider}{a.environment === 'test' ? ' (test mode — no real money)' : ''}
            </p>
            <p className="text-sm text-gray-600">
              {a.mode === 'collect'
                ? 'Gympe collects the payment and pays it out to your bank account, minus Gympe’s fee. Each payout comes with a statement.'
                : 'Payments go straight to your own merchant account. Gympe’s fee is billed on your monthly Gympe invoice.'}
            </p>
            <p className="text-xs text-gray-500">Gympe’s invoices, including these fees, appear under Expenses → “Gympe fees & subscription”.</p>
          </div>
        ) : (
          <p className="text-sm text-amber-700">{PROBLEMS[settings.problem ?? ''] ?? 'Online payments are not active yet.'} Contact Gympe support.</p>
        )}
      </div>

      {settings.canChoose && settings.options.length > 1 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Where should payments go?</h3>
          {settings.options.map(o => {
            const selected = settings.currentChoiceAccountId === o.accountId;
            return (
              <button
                key={o.accountId}
                type="button"
                disabled={selected || !!saving}
                onClick={() => choose(o.accountId)}
                className={`w-full text-left rounded-xl border p-4 transition-colors ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'} disabled:cursor-default`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{o.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {o.mode === 'collect' ? 'Gympe collects and pays out to you' : 'Straight to your own account'} · {o.detail}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-blue-700 shrink-0">{selected ? 'In use' : saving === o.accountId ? 'Switching…' : 'Use this'}</span>
                </div>
              </button>
            );
          })}
          <p className="text-xs text-gray-500">Links already sent keep working and are paid into the account they were created with.</p>
        </div>
      )}
    </div>
  );
}
